import { Input, makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { MainContext, useDialogService, useMainContext, useT } from '@powerportalspro/react';
import type { TableRecord } from '@powerportalspro/core';
import {
  GridColumn,
  GridColumns,
  GridTemplateColumn,
  MainGrid,
  MenuBar,
  RefreshContextButton,
  resolveColumnDisplayValue,
  SaveContextButton,
  type CustomViewDefinition,
} from '@powerportalspro/react-fluent';
import { useState } from 'react';

// Mirror Blazor's `GridEditingMainDemo` keys so both stacks share the same
// JSON. Framework flattens to `components.<FullTypeName>.<key>`.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.GridEditing.GridEditingMainDemo';

// Thrown from `onBeforeSave` to short-circuit the real save round-trip — caught
// in `onError` so the user sees the mock-save dialog instead of the framework's
// "Save failed" treatment. Same pattern as FileGridMainSample / the Blazor
// demos' `CancelEventArgs.Cancel = true` flow.
const MOCK_SAVE_SENTINEL = '__mock-save-cancelled__';

// Targets the demo's `ppp_contact` table (a sandbox contacts clone every
// signed-in user can edit) so the editing demo never touches real contact
// records. Columns are the `ppp_`-prefixed equivalents of the standard contact
// fields; `ppp_parentaccount` is a lookup to `ppp_account`.
const ALL_CONTACTS_VIEW: CustomViewDefinition = {
  id: '00000000-0000-0000-0000-000000000001',
  tableName: 'ppp_contact',
  displayName: 'All Contacts',
  columns: [
    { columnName: 'ppp_firstname', width: 220 },
    { columnName: 'ppp_email', width: 220 },
    { columnName: 'ppp_mobilephone', width: 160 },
    { columnName: 'ppp_age', width: 100 },
    { columnName: 'ppp_parentaccount', width: 200 },
  ],
  fetchXml: `<fetch>
    <entity name="ppp_contact">
      <attribute name="ppp_firstname" />
      <attribute name="ppp_lastname" />
      <attribute name="ppp_email" />
      <attribute name="ppp_mobilephone" />
      <attribute name="ppp_age" />
      <attribute name="ppp_parentaccount" />
      <order attribute="ppp_lastname" />
    </entity>
  </fetch>`,
};

const useStyles = makeStyles({
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: '11px',
    fontWeight: 600,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

function readString(record: TableRecord | null | undefined, column: string): string {
  if (!record) return '';
  return resolveColumnDisplayValue(record, column) ?? '';
}

// Edit mode must read the RAW editable value — the property `setValue` writes —
// NOT resolveColumnDisplayValue, which prefers `formattedValues` (never updated
// by setValue) and applies display formatting. Reading the display value makes
// the controlled input show a stale string while typing (the change dirties the
// record but never appears). Mirrors the framework's own `useStringColumn` and
// Blazor's `PrimaryRecord.GetValueOrDefault<StringValue>().Value`.
function readEditableString(record: TableRecord | null | undefined, column: string): string {
  const prop = record?.properties?.[column] as { value?: unknown } | undefined;
  return typeof prop?.value === 'string' ? prop.value : '';
}

// Outer component mounts the MainContext + warning; the editable grid and the
// save toolbar live in the inner body so they can read the context via
// `useMainContext` (a provider component can't consume its own context).
export function GridEditingMainSample() {
  const t = useT();
  return (
    <MainContext warnOnUnsavedChanges={false}>
      <MessageBar
        intent="warning"
        layout="multiline"
        style={{ gridTemplateRows: 'auto', minHeight: 'auto', marginBottom: tokens.spacingVerticalM }}
      >
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>
      <GridEditingSampleBody />
    </MainContext>
  );
}

function GridEditingSampleBody() {
  const styles = useStyles();
  const t = useT();
  const dialog = useDialogService();
  const ctx = useMainContext();

  // Controlled editable state, defaulted to `true` so the grid starts in edit
  // mode (the React `editable` prop is controlled-only — there's no
  // uncontrolled default-on flavor). `onEditableChanged` keeps the gear-menu
  // toggle working.
  const [editable, setEditable] = useState(true);

  return (
    <>
      <MenuBar style={{ marginBottom: tokens.spacingVerticalM }}>
        <SaveContextButton
          appearance="primary"
          onBeforeSave={() => {
            throw new Error(MOCK_SAVE_SENTINEL);
          }}
          onError={async (err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            if (message === MOCK_SAVE_SENTINEL) {
              await dialog.showInfoAsync(t(`${STRINGS_BASE}.mock-save-message`));
              await ctx?.refreshAll();
              return;
            }
            await dialog.showErrorAsync(message, { title: 'Save failed' });
          }}
        />
        <RefreshContextButton />
      </MenuBar>

      <MainGrid
        tableName="ppp_contact"
        minHeight="400px"
        maxHeight="400px"
        pageSize={10}
        allowEdit
        editable={editable}
        onEditableChanged={setEditable}
        customViewDefinitions={[ALL_CONTACTS_VIEW]}
        defaultViewId={ALL_CONTACTS_VIEW.id}
        viewIds={[ALL_CONTACTS_VIEW.id]}
      >
        <GridColumns>
          {/* Custom-widget edit template — collapses ppp_firstname +
              ppp_lastname into a single text field; the change handler splits
              on space and writes both columns back via setValue. */}
          <GridTemplateColumn
            displayName="Full Name"
            width="220px"
            dependsOn={['ppp_firstname', 'ppp_lastname']}
            sortBy="ppp_lastname"
            cellRenderer={({ record }) => {
              const first = readString(record, 'ppp_firstname');
              const last = readString(record, 'ppp_lastname');
              const initials = (first.charAt(0) + last.charAt(0)).toUpperCase();
              const display = `${first} ${last}`.trim();
              return (
                <div className={styles.nameRow}>
                  <span className={styles.avatar}>{initials || '—'}</span>
                  <strong>{display || '—'}</strong>
                </div>
              );
            }}
            editRenderer={({ record, setValue }) => {
              const first = readEditableString(record, 'ppp_firstname');
              const last = readEditableString(record, 'ppp_lastname');
              // No trim, no template-literal join: a trailing space typed by the
              // user lands in `last` as "Smith " and must survive the
              // round-trip to re-appear in the input. `${first} ${last}` would
              // also inject a phantom space when last is empty (showing "John "
              // for first-name-only edits), so build the display with an
              // explicit join that omits empty segments.
              const combined = [first, last].filter(Boolean).join(' ');
              return (
                <Input
                  value={combined}
                  onChange={(_, data) => {
                    const next = data.value ?? '';
                    const idx = next.indexOf(' ');
                    const head = idx === -1 ? next : next.slice(0, idx);
                    const tail = idx === -1 ? '' : next.slice(idx + 1);
                    setValue('ppp_firstname', { $type: 14, value: head });
                    setValue('ppp_lastname', { $type: 14, value: tail || null });
                  }}
                />
              );
            }}
          />

          {/* Bare GridColumns — no cellRenderer, so the grid uses its default
              formatted-value display in read mode and auto-dispatches the
              metadata-driven inline editor in edit mode (TextEdit for the
              strings, NumberEdit for the integer age, LookupEdit for the
              ppp_account lookup). */}
          <GridColumn columnName="ppp_email" displayName="Email" width="220px" />
          <GridColumn columnName="ppp_mobilephone" displayName="Phone" width="160px" />
          <GridColumn columnName="ppp_age" displayName="Age" width="100px" />
          <GridColumn columnName="ppp_parentaccount" displayName="Account" width="200px" />
        </GridColumns>
      </MainGrid>
    </>
  );
}
